/**
 * ToastContainer.tsx — SPINEX Predict admin toast stack
 *
 * Fixes vs original:
 *  1. Only handled success/error. Now handles info and warning too, matching
 *     the expanded ToastType union in useToast.ts.
 *  2. Added role="status" aria-live="polite" so screen readers announce toasts.
 *  3. Added keyboard-accessible dismiss (the X button was missing aria-label
 *     in some builds — confirmed present, kept it).
 *  4. Max 5 visible toasts — excess are trimmed from the bottom of the stack
 *     to prevent the screen flooding on rapid-fire API errors.
 */

import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '@/utils'
import type { Toast } from '@/hooks/useToast'

interface ToastContainerProps {
  toasts: Toast[]
  onRemove: (id: string) => void
}

const MAX_VISIBLE = 5

const typeConfig = {
  success: {
    icon: CheckCircle,
    className: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400',
  },
  error: {
    icon: AlertTriangle,
    className: 'bg-red-500/15 border-red-500/30 text-red-400',
  },
  warning: {
    icon: AlertTriangle,
    className: 'bg-amber-500/15 border-amber-500/30 text-amber-400',
  },
  info: {
    icon: Info,
    className: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
  },
} as const

export function ToastContainer({ toasts, onRemove }: ToastContainerProps) {
  const visible = toasts.slice(-MAX_VISIBLE)
  if (visible.length === 0) return null

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-4 right-4 z-[70] flex flex-col gap-2 pointer-events-none"
    >
      {visible.map((toast) => {
        const { icon: Icon, className } = typeConfig[toast.type] ?? typeConfig.info
        return (
          <div
            key={toast.id}
            className={cn(
              'flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-semibold shadow-xl',
              'animate-in fade-in slide-in-from-bottom-2 min-w-[280px] max-w-sm pointer-events-auto',
              className,
            )}
          >
            <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => onRemove(toast.id)}
              className="text-current/60 hover:text-current transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
              aria-label="Dismiss notification"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
